import type { FC, PropsWithChildren, ReactNode } from 'react';
import { Children, isValidElement, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LayoutChangeEvent, ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native';
import { Card } from 'react-native-paper';

import { usePostCard } from '@/features/posts/components/feed/PostCardContext';
import { PostCardLink } from '@/features/posts/components/feed/PostCardLink';
import useIsPostcardNew from '@/features/posts/hooks/useIsNewPostCard';
import { PostsRoute } from '@/features/posts/navigation/postsRoute';
import { postCardContentStyle } from '@/features/posts/styles/postCardPointer';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    card: {
      marginBottom: 12,
      backgroundColor: theme.colors.surfaceContainerLowest
    },
    inner: {
      paddingTop: 0,
      paddingHorizontal: 0,
      ...postCardContentStyle
    }
  });

export type PostCardProps = {
  cardStyle?: ViewStyle;
  cardInnerStyle?: ViewStyle;
  onHeightChange?: (height: number) => void;
  flat?: boolean;
};

const Content = ({ children }: PropsWithChildren) => <>{children}</>;
Content.displayName = 'PostCard.Content';

const Actions = ({ children }: PropsWithChildren) => <>{children}</>;
Actions.displayName = 'PostCard.Actions';

const PostCardComponent: FC<PropsWithChildren<PostCardProps>> = memo(
  ({ cardStyle, cardInnerStyle, onHeightChange, flat = false, children }) => {
    const theme = useAppTheme();
    const { postId, creatorOrSiteName, title, text } = usePostCard();
    const styles = useMemo(() => makeStyles(theme), [theme]);
    const isNew = useIsPostcardNew();
    const { t } = useTranslation(LocalizationNameSpace.POSTS, {
      keyPrefix: 'screens.post-feed'
    });

    let content: ReactNode;
    let actions: ReactNode;

    Children.forEach(children, (child) => {
      if (isValidElement<PropsWithChildren>(child)) {
        if (child.type === Content) {
          content = child.props.children;
        } else if (child.type === Actions) {
          actions = child.props.children;
        }
      }
    });

    const onLayout = useCallback(
      (event: LayoutChangeEvent) =>
        onHeightChange?.(event.nativeEvent.layout.height),
      [onHeightChange]
    );

    const navigationParams = useMemo(() => ({ id: postId }), [postId]);
    const cardStyles = useMemo(
      () => [styles.card, cardStyle],
      [styles.card, cardStyle]
    );

    const cardElevation = useMemo(() => {
      if (flat) return 0;
      return isNew ? 2 : 1;
    }, [flat, isNew]);

    return (
      <Card
        mode="elevated"
        elevation={cardElevation}
        style={cardStyles}
        testID="post-feed-card"
      >
        {/* eslint-disable-next-line react-native-a11y/has-accessibility-hint */}
        <PostCardLink
          screen={PostsRoute.PostDetail}
          params={navigationParams}
          accessibilityLabel={t('openPost', {
            name: creatorOrSiteName ?? title ?? text ?? ''
          })}
        >
          <Card.Content
            style={[styles.inner, cardInnerStyle]}
            onLayout={onLayout}
          >
            {content}
          </Card.Content>
        </PostCardLink>
        {actions}
      </Card>
    );
  }
);
PostCardComponent.displayName = 'PostCard';

export const PostCard = PostCardComponent as FC<
  PropsWithChildren<PostCardProps>
> & {
  Content: FC<PropsWithChildren>;
  Actions: FC<PropsWithChildren>;
};

PostCard.Content = Content;
PostCard.Actions = Actions;
